import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Wallet, Route, Award, Handshake, Scale, ShieldCheck,
  FolderInput, Landmark, TrendingUp, Building2, Users, Gavel, Bell, LifeBuoy,
  ClipboardList, LogOut, ScrollText, MapPinned, PieChart, PackageSearch, ListChecks,
  Menu, X, Settings as SettingsIcon
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { ThemePaletteToggle } from "@/components/ThemePaletteToggle";

const linkFor = (role) => {
  const common = [
    { to: "/dashboard", label: "dashboard", icon: LayoutDashboard, key: "dash" },
  ];
  const borrower = [
    { to: "/borrower/passport", label: "financial_passport", icon: FileText, key: "passport" },
    { to: "/borrower/readiness", label: "readiness_report", icon: Award, key: "readiness" },
    { to: "/borrower/funding", label: "funding_requirements", icon: Wallet, key: "funding" },
    { to: "/borrower/routes", label: "financing_routes", icon: Route, key: "routes" },
    { to: "/borrower/schemes", label: "scheme_matches", icon: ScrollText, key: "schemes" },
    { to: "/borrower/interests", label: "lender_interest", icon: Handshake, key: "interests" },
    { to: "/borrower/compare", label: "offer_comparison", icon: Scale, key: "compare" },
    { to: "/borrower/consent", label: "consent_centre", icon: ShieldCheck, key: "consent" },
    { to: "/borrower/applications", label: "applications", icon: ClipboardList, key: "apps" },
    { to: "/borrower/documents", label: "documents", icon: FolderInput, key: "docs" },
    { to: "/borrower/subsidy", label: "subsidy_tracker", icon: TrendingUp, key: "subsidy" },
    { to: "/borrower/grievances", label: "grievances", icon: LifeBuoy, key: "griev" },
  ];
  const bank = [
    { to: "/bank/opportunities", label: "opportunity_marketplace", icon: PackageSearch, key: "opps" },
    { to: "/bank/applications", label: "applications", icon: ClipboardList, key: "apps" },
    { to: "/bank/policies", label: "policies", icon: ListChecks, key: "policies" },
    { to: "/bank/analytics", label: "analytics", icon: PieChart, key: "analytics" },
  ];
  const admin = [
    { to: "/admin/users", label: "users", icon: Users, key: "users" },
    { to: "/admin/schemes", label: "schemes", icon: ScrollText, key: "schemes" },
    { to: "/admin/lenders", label: "lenders", icon: Landmark, key: "lenders" },
    { to: "/admin/grievances", label: "grievances", icon: LifeBuoy, key: "griev" },
    { to: "/admin/audit", label: "audit_logs", icon: Gavel, key: "audit" },
  ];
  const gov = [
    { to: "/gov/district-map", label: "district_map", icon: MapPinned, key: "map" },
    { to: "/gov/sector", label: "sector_analysis", icon: PieChart, key: "sector" },
    { to: "/gov/schemes", label: "scheme_utilization", icon: ScrollText, key: "sch" },
    { to: "/gov/inclusion", label: "inclusion_metrics", icon: Building2, key: "incl" },
  ];
  const facil = [
    { to: "/facilitator/borrowers", label: "borrowers", icon: Users, key: "brw" },
  ];
  if (role === "borrower") return [...common, ...borrower, { to: "/settings", label: "settings", icon: SettingsIcon, key: "settings" }];
  if (role === "bank_admin" || role === "bank_rm") return [...common, ...bank, { to: "/settings", label: "settings", icon: SettingsIcon, key: "settings" }];
  if (role === "platform_admin") return [...common, ...admin, { to: "/settings", label: "settings", icon: SettingsIcon, key: "settings" }];
  if (role === "government") return [...common, ...gov, { to: "/settings", label: "settings", icon: SettingsIcon, key: "settings" }];
  if (role === "facilitator") return [...common, ...facil, { to: "/settings", label: "settings", icon: SettingsIcon, key: "settings" }];
  return [...common, { to: "/settings", label: "settings", icon: SettingsIcon, key: "settings" }];
};

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const loc = useLocation();
  const nav = useNavigate();
  const links = linkFor(user?.role);

  const roleTitle = user?.role ? t(`role_${user.role}`) : "";

  const handleLogout = async () => {
    await logout();
    // Hard reload to fully purge in-memory state and any cached queries.
    window.location.assign("/");
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  // Grievance/support route varies by role.
  const grievanceHref =
    user?.role === "borrower" ? "/borrower/grievances"
    : user?.role === "platform_admin" ? "/admin/grievances"
    : user?.role === "facilitator" ? "/borrower/grievances"
    : "/contact";

  return (
    <div className="min-h-screen flex brand-body" style={{ background: "var(--bk-ivory)" }} data-testid="dashboard-layout">
      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-overlay"
        />
      )}
      {/* Sidebar (desktop static, mobile drawer) */}
      <aside
        className={`fixed z-50 lg:sticky lg:z-auto top-0 h-screen w-64 shrink-0 text-slate-200 flex flex-col transition-transform duration-200 ease-out ${drawerOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        style={{ background: "var(--bk-navy-2)" }}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <Link to="/" className="block">
            <div className="brand-heading font-semibold text-white text-lg leading-tight" data-testid="brand-name">BharatKosh</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5">Loan Bazaar · MP</div>
          </Link>
          <button
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden text-white/70 hover:text-white p-1 rounded-md"
            aria-label="Close menu"
            data-testid="drawer-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {links.map((l) => {
            const Icon = l.icon;
            const active = loc.pathname === l.to || (l.to !== "/dashboard" && loc.pathname.startsWith(l.to));
            return (
              <Link
                key={l.key}
                to={l.to}
                data-testid={`nav-${l.key}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  active ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span>{t(l.label)}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/45 mb-2 truncate">{user?.email}</div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-sm text-white/75 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-[color:var(--bk-ivory)]/90 backdrop-blur-xl border-b border-[color:var(--bk-line)]">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3 lg:py-4 gap-3">
            <button
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-[color:var(--bk-line)] bg-white text-[color:var(--bk-navy)]"
              aria-label="Open menu"
              data-testid="drawer-open"
            >
              <Menu className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/55 truncate">{roleTitle}</div>
              <div className="brand-heading text-base sm:text-xl font-semibold text-[color:var(--bk-navy)] leading-tight truncate">{user?.name}</div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Team Demo Role Switcher */}
              <div className="hidden sm:flex items-center gap-1.5 bg-white border border-[color:var(--bk-line)] rounded-lg px-2 py-1 text-xs shadow-sm">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Demo Role:</span>
                <select
                  value={user?.role || "borrower"}
                  onChange={(e) => {
                    const rolesMap = {
                      borrower: { email: "borrower@bharatkosh.demo", name: "Rajesh Sharma", path: "/dashboard" },
                      bank_admin: { email: "bankadmin@bharatkosh.demo", name: "Amita Verma", path: "/bank/dashboard" },
                      platform_admin: { email: "admin@bharatkosh.demo", name: "Platform Admin", path: "/admin/schemes" },
                      government: { email: "government@bharatkosh.demo", name: "MSME Dept MP", path: "/gov/district-map" },
                    };
                    const sel = rolesMap[e.target.value];
                    if (sel) {
                      localStorage.setItem("bk_current_user", JSON.stringify({
                        id: "usr-" + e.target.value,
                        email: sel.email,
                        name: sel.name,
                        role: e.target.value,
                        mobile: "9876543210",
                        active: true
                      }));
                      window.location.assign(sel.path);
                    }
                  }}
                  className="bg-transparent font-medium text-slate-800 outline-none cursor-pointer"
                  title="Switch between roles for team demo"
                >
                  <option value="borrower">👤 MSME Borrower</option>
                  <option value="bank_admin">🏦 Bank Desk</option>
                  <option value="platform_admin">⚙️ CMS / Admin</option>
                  <option value="government">🏛️ Government Monitor</option>
                </select>
              </div>

              <ThemePaletteToggle />
              <LanguageToggle />
              <Link to={grievanceHref} data-testid="header-help" className="text-[color:var(--bk-navy)]/60 hover:text-[color:var(--bk-navy)] transition-colors">
                <Bell className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
